import React, { useState, useEffect, useCallback } from 'react'
import './EventsNearMe.css'
import { Typography, Button, Dropdown, Card, MenuProps } from 'antd'
import {
    CalendarOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useApiQuery } from '../../../../hooks/useApiQuery'
import { EventData } from '../../../../types/events'
import { useCities } from '../../../../hooks/useCities'
import { City } from '../../../Common/CitySelector'
import { formatEventDate } from '../../../../utils/dateUtils'
import { trackButtonClick } from '../../../../utils/gtmUtils'

const { Title, Paragraph } = Typography

interface EventsNearMeProps {
    onSportEventClick: (event: EventData) => void
    onOthersClick: () => void
    onViewMoreClick?: () => void
}

const EventsNearMe: React.FC<EventsNearMeProps> = ({
    onSportEventClick,
    onOthersClick,
    onViewMoreClick,
}) => {
    const { t } = useTranslation()
    const [selectedCity, setSelectedCity] = useState<string>('')
    const [selectedCityId, setSelectedCityId] = useState<string>('')
    const [cityOptions, setCityOptions] = useState<MenuProps['items']>([])
    const [citiesMap, setCitiesMap] = useState<Map<string, City>>(new Map())
    const {
        cities,
        isLoading: isLoadingCities,
        error: citiesError,
    } = useCities() // Use shared cities hook

    const { data: events, isPending: isLoadingEvents } = useApiQuery<
        EventData[]
    >({
        endpoint: selectedCityId
            ? `website/events?cityId=${selectedCityId}`
            : '',
        method: 'GET',
        queryKey: ['events', selectedCityId],
        enabled: !!selectedCityId,
    })

    const getCityLabel = useCallback(
        (cityKey: string): string => {
            const cityMap: Record<string, string> = {
                Cologne: t('home.eventsNearMe.cities.cologne'),
                Munich: t('home.eventsNearMe.cities.munich'),
                Düsseldorf: t('home.eventsNearMe.cities.dusseldorf'),
                Frankfurt: t('home.eventsNearMe.cities.frankfurt'),
                Others: t('home.eventsNearMe.cities.others'),
            }
            return cityMap[cityKey] || cityKey
        },
        [t]
    )

    useEffect(() => {
        if (!cities || cities.length === 0) return

        // Create a map of cityName to City for easy lookup
        const citiesMap = new Map<string, City>()
        cities.forEach((city) => {
            citiesMap.set(city.cityName, city)
        })
        setCitiesMap(citiesMap)

        // Transform API response to dropdown format with translations
        const options: MenuProps['items'] = [
            ...cities.map((city: City) => ({
                key: city.cityName,
                label: getCityLabel(city.cityName),
            })),
            { key: 'Others', label: t('home.eventsNearMe.cities.others') },
        ]

        setCityOptions(options)

        // Set default selected city to first city if available
        if (cities.length > 0 && !selectedCity) {
            setSelectedCity(cities[0].cityName)
            setSelectedCityId(cities[0].cityId)
        }
    }, [cities, selectedCity, t, getCityLabel])

    const handleCityChange = ({ key }: { key: string }) => {
        if (key === 'Others') {
            onOthersClick()
        } else {
            setSelectedCity(key)
            // Get cityId from the cities map
            const city = citiesMap.get(key)
            if (city) {
                setSelectedCityId(city.cityId)
            }
        }
    }

    return (
        <div className="sportevents-container" id="events">
            {/* Header Row: Title + Location Button */}
            <div className="sportevents-header">
                <Title className="title" level={2}>
                    {t('home.eventsNearMe.title')}
                </Title>
                <Dropdown
                    menu={{
                        items: cityOptions || [],
                        onClick: handleCityChange,
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    disabled={
                        isLoadingCities ||
                        !cityOptions ||
                        cityOptions.length === 0 ||
                        !!citiesError
                    }
                >
                    <Button
                        type="text"
                        icon={<EnvironmentOutlined />}
                        iconPosition="end"
                        className="city-dropdown-button"
                        loading={isLoadingCities}
                    >
                        {selectedCity
                            ? getCityLabel(selectedCity)
                            : citiesError
                              ? t('home.eventsNearMe.citiesUnavailable')
                              : t('common.loading')}
                    </Button>
                </Dropdown>
            </div>

            {/* Events List */}
            <div className="sportevents-list">
                {isLoadingEvents ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Paragraph>{t('common.loading')}</Paragraph>
                    </div>
                ) : !events || events.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Paragraph>{t('home.eventsNearMe.noEvents')}</Paragraph>
                    </div>
                ) : (
                    (events || []).map((event) => {
                        const { date, time } = formatEventDate(event.eventDate)
                        return (
                            <Card
                                className="sportevents-card"
                                key={event.eventId}
                                hoverable
                                style={{ padding: 0 }}
                                onClick={() => onSportEventClick(event)}
                            >
                                <div className="sportevents-card-content">
                                    {/* Image*/}
                                    <div className="card-image">
                                        <img
                                            className="card-image-bg"
                                            src={
                                                event.image ||
                                                '/img/Footballcard.png'
                                            }
                                            alt={
                                                event.title ||
                                                t('common.alt.sportEvent')
                                            }
                                            onError={(e) => {
                                                // Fallback to default image if API image fails to load
                                                const target =
                                                    e.target as HTMLImageElement
                                                if (
                                                    target.src !==
                                                    '/img/Footballcard.png'
                                                ) {
                                                    target.src =
                                                        '/img/Footballcard.png'
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* Content*/}
                                    <div className="card-text">
                                        <Title className="title" level={4}>
                                            {event.sportType.sportName}
                                        </Title>

                                        <Paragraph className="paragraph">
                                            {event.title}
                                        </Paragraph>

                                        {/* Metadata*/}
                                        <div className="card-metadata">
                                            <div className="metadata-item">
                                                <CalendarOutlined />
                                                <span>
                                                    {t(
                                                        'home.eventsNearMe.metadata.date'
                                                    )}
                                                </span>
                                                <span>{date}</span>
                                            </div>

                                            <div className="metadata-item">
                                                <ClockCircleOutlined />
                                                <span>
                                                    {t(
                                                        'home.eventsNearMe.metadata.time'
                                                    )}
                                                </span>
                                                <span>{time}</span>
                                            </div>

                                            <div className="metadata-item">
                                                <EnvironmentOutlined />
                                                <span>
                                                    {t(
                                                        'home.eventsNearMe.metadata.location'
                                                    )}
                                                </span>
                                                <span>{event.place.name}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )
                    })
                )}
            </div>

            {/* View More Button */}
            <div className="view-more-button">
                <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                        trackButtonClick('view_more', 'events_near_me')
                        onViewMoreClick?.()
                    }}
                >
                    {t('home.eventsNearMe.viewMore')}
                </Button>
            </div>
        </div>
    )
}

export default EventsNearMe
